import { DateTime, Interval } from "luxon";
import {
  ManagedReceivingPartnerApi as Api,
  SharedApiV2,
} from "@capstone/mock-api";
import {
  ReservationDetailDto,
  ReservationListDto,
  ReservationOccurrenceDto,
} from "mock-api/src/shared-v2-types";
import { DropLoadAvailability, ManagedType } from "src/app/core/constants";
import { Day } from "src/app/core/day.model";
import { ChangesHistory } from "src/app/core/history.model";
import { formatWeekdays } from "src/app/core/pipes/weekdays.pipe";
import { Site } from "src/app/core/sites";
import { TimeOfDay } from "src/app/core/time-of-day.model";
import { Weekdays } from "src/app/core/weekdays.model";
import { AppointmentReservationSlot } from "src/app/partner/appointments/fields/appointment-reservation-slot.model";
import { AppointmentSlot } from "src/app/partner/appointments/fields/appointment-slot.model";
import {
  CarrierReference,
  deserializeSimpleCarrier,
  SimpleCarrier,
} from "src/app/partner/global/carriers";
import { Door } from "src/app/partner/settings/doors/door.model";
import { getVendorRouteUrl } from "src/app/partner/vendors/base-vendor.model";
import { deserializeVendorDisplayName } from "src/app/partner/vendors/vendor.model";
import {
  formatHistoryIdChangeValue,
  getApiDetailsDecorator,
  parseDateTime,
  parseDuration,
  throwUnhandledCaseError,
} from "src/utils";
import { AppointmentUpdatePurchaseOrder } from "../../appointments/appointment-update.model";
import {
  BaseReservationV2,
  ReservationCarrier,
  ReservationVendor,
} from "./base-reservation-v2.model";

const api = getApiDetailsDecorator<SharedApiV2.ReservationDetailDto>();

interface DeserializeArgs {
  doors: readonly Door[];
  site: Site;
}

export enum ReservationType {
  Carrier = "Carrier",
  Vendor = "Vendor",
}

export class ReservationV2 extends BaseReservationV2 {
  private constructor(
    args: Omit<
      ClassProperties<ReservationV2>,
      "displayName" | "type" | "isExpired" | "effectiveDayRange"
    >,
  ) {
    super(args);
    this.carriers = args.carriers;
    this.createdDate = args.createdDate;
    this.endTime = args.endTime;
    this.exceptionDays = args.exceptionDays;
    this.doors = args.doors;
    this.history = args.history;
    this.id = args.id;
    this.isExpiringSoon = args.isExpiringSoon;
    this.modifiedDate = args.modifiedDate;
    this.site = args.site;
    this.vendors = args.vendors;

    // Computed Properties
    this.effectiveDayRange = this.startDay
      .toDateTime(Day.startOfDay, this.site.timeZone)
      .until(
        this.endDay
          // Make inclusive by offsetting to the start of the next day.
          .plus({ days: 1 })
          .toDateTime(Day.startOfDay, this.site.timeZone),
      );

    this.isExpired = this.endDay.isBefore(Day.getTodayIn(this.site.timeZone));

    if (this.vendors.length > 0) {
      this.type = ReservationType.Vendor;
      this.displayName =
        this.name || this.vendors.map((vendor) => vendor.name).join(", ");
    } else if (this.carriers.length > 0) {
      this.type = ReservationType.Carrier;
      this.displayName =
        this.name || this.carriers.map((carrier) => carrier.name).join(", ");
    } else {
      throw new Error("No carrier or vendor set on reservation.");
    }
  }

  @api() public readonly carriers: readonly ReservationCarrier[];
  @api() public readonly createdDate: DateTime;
  @api() public readonly doors: readonly Door[];
  public readonly endTime: TimeOfDay;
  @api({ key: "exceptions" }) public readonly exceptionDays: Day[];
  @api() public readonly history: readonly ChangesHistory[];
  @api() public readonly id: number;
  @api({ key: "expiringSoon" }) public readonly isExpiringSoon: boolean;
  @api() public readonly modifiedDate: DateTime;
  public override readonly site: Site;
  @api() public readonly vendors: readonly ReservationVendor[];

  /** A display name such as for the schedule reservation bar. */
  public readonly displayName: string;
  /** Whether the reservation has exceeded its end date. */
  public readonly isExpired: boolean;
  public readonly type: ReservationType;
  public readonly effectiveDayRange: Interval;

  public static deserialize(
    data: ReservationOccurrenceDto | ReservationListDto | ReservationDetailDto,
    // TODO: Remove the doors argument and instead have the API return the selectedDoor in the reservation data.
    { doors, site }: DeserializeArgs,
  ): ReservationV2 {
    // As per business, reservation will only contain one door.
    const apiDoor = data.doors[0];
    const selectedDoor = doors.find((door) => door.id === apiDoor.id);
    if (!selectedDoor) {
      throw new Error(
        `Could not find reservation door with ID "${apiDoor.id}".`,
      );
    }

    const history =
      "history" in data
        ? ChangesHistory.deserializeList<HistoryFieldName>(data.history, {
            fieldDefinitions: {
              carrier: {
                formatChangeValue: formatHistoryIdChangeValue,
                label: "Carriers",
              },
              dayOfWeek: {
                label: "Days",
                formatChangeValue: (value) =>
                  value
                    ? formatWeekdays(Weekdays.deserialize(value), "short")
                    : null,
              },
              door: {
                formatChangeValue: formatHistoryIdChangeValue,
                label: "Door",
              },
              exceptions: {
                formatChangeValue: (value) =>
                  value
                    ?.split(",")
                    .map((date) => Day.deserialize(date, null)) ?? null,
              },
              vendor: {
                formatChangeValue: formatHistoryIdChangeValue,
                label: "Vendors",
              },
            },
          })
        : [];

    const exceptionDays =
      "exceptions" in data
        ? data.exceptions.map((exception) =>
            Day.deserialize(exception, site.timeZone),
          )
        : [];

    return new ReservationV2({
      ...this.deserializeBase(data, { site }),
      carriers: data.carriers.map((carrier) =>
        deserializeSimpleCarrier(carrier),
      ),
      createdDate: parseDateTime(data.createdDate, site),
      doors: [selectedDoor],
      exceptionDays,
      endTime: TimeOfDay.deserialize(data.startTime).addDuration(
        parseDuration(data.length, "minutes"),
      ),
      history,
      id: data.id,
      isExpiringSoon: data.expiringSoon,
      modifiedDate: parseDateTime(data.modifiedDate, site),
      vendors: data.vendors.map((vendor) =>
        deserializeReservationVendor(vendor, { site }),
      ),
    });
  }

  public static deserializeList(
    value: ReservationListDto[],
    args: DeserializeArgs,
  ): readonly ReservationV2[] {
    return value.map((reservation) =>
      ReservationV2.deserialize(reservation, args),
    );
  }

  public isInEffectOn(day: Day): boolean {
    return (
      this.effectiveDayRange.contains(
        day.toDateTime(Day.startOfDay, this.site.timeZone),
      ) &&
      !this.exceptionDays
        .map((exceptionDay) => exceptionDay.toString())
        .includes(day.toString())
    );
  }

  public includesDay(day: Day): boolean {
    return this.weekdays.isSelected(day) && this.isInEffectOn(day);
  }

  public getInstanceOn(day: Day): Interval | null {
    if (!this.includesDay(day)) {
      return null;
    }

    const start = day.toDateTime(this.startTime, this.site.timeZone);
    const endDay = this.endTime.isBefore(this.startTime)
      ? day.plus({ days: 1 })
      : day;
    const end = endDay.toDateTime(this.endTime, this.site.timeZone);
    return start.until(end);
  }

  public getInstanceContaining(time: DateTime): Interval | null {
    const day = new Day(time);
    const sameDayInstance = this.getInstanceOn(day);
    const previousDayInstance = this.getInstanceOn(day.minus({ days: 1 }));

    // Either the time is on the same day as (the start of) the reservation, or
    // the reservation spans over into the next day and the time is in the
    // second half of the reservation in the following day. These shouldn't
    // overlap so check each in turn to find the one that contains it. If it's
    // not in either, then it's not in a reservation from this series.
    if (sameDayInstance?.contains(time) === true) {
      return sameDayInstance;
    } else if (previousDayInstance?.contains(time) === true) {
      return previousDayInstance;
    } else {
      return null;
    }
  }

  public includesSlot(
    slot: AppointmentSlot | AppointmentReservationSlot,
  ): boolean {
    return (
      slot.doors.some((door) =>
        this.doors.map((d) => d.id).includes(door.id),
      ) && this.getInstanceContaining(slot.startTime) !== null
    );
  }

  public isFor({
    carrierOfRecord,
    deliveryCarrier,
    orders,
  }: {
    readonly carrierOfRecord: CarrierReference;
    readonly deliveryCarrier?: SimpleCarrier | null;
    readonly orders: readonly AppointmentUpdatePurchaseOrder[];
  }): boolean {
    switch (this.type) {
      case ReservationType.Carrier: {
        return this.carriers.some(
          (carrier) =>
            carrier.id === carrierOfRecord.id ||
            carrier.id === deliveryCarrier?.id,
        );
      }
      case ReservationType.Vendor: {
        const hasMatchingCarrier = this.carriers.some(
          (carrier) =>
            carrier.id === carrierOfRecord.id ||
            carrier.id === deliveryCarrier?.id,
        );
        const hasVendorInOrders = this.vendors.some((vendor) =>
          isVendorInOrders(vendor, orders),
        );
        return (
          (this.carriers.length === 0 || hasMatchingCarrier) &&
          hasVendorInOrders
        );
      }
      default: {
        throwUnhandledCaseError("reservation type", this.type);
      }
    }
  }

  /**
   * Whether this reservation can be used with orders and/or appointments with
   * the given values associated with them.
   *
   * @param options - The values to check against the reservation.
   */
  public isCompatibleWith({
    isDropLoad,
    managedType,
  }: {
    /** The drop load state to check against the reservation. */
    readonly isDropLoad?: boolean;
    /** The managed type to check against the reservation. */
    readonly managedType?: ManagedType;
  }): boolean {
    return (
      (isDropLoad === undefined || this.isCompatibleWithDropLoad(isDropLoad)) &&
      (managedType === undefined ||
        this.isCompatibleWithManagedType(managedType))
    );
  }

  public isCompatibleWithDropLoad(isDropLoad: boolean): boolean {
    switch (this.dropLoadAvailability) {
      case DropLoadAvailability.Optional:
        return true;
      case DropLoadAvailability.Mandatory:
        return isDropLoad;
      case DropLoadAvailability.NotAllowed:
        return !isDropLoad;
      default:
        throwUnhandledCaseError(
          "drop load availability",
          this.dropLoadAvailability,
        );
    }
  }

  public isCompatibleWithManagedType(managedType: ManagedType): boolean {
    // No managed type on the slot means it can be used with any orders.
    return this.managedType === null || managedType === this.managedType;
  }

  public getRouteUrl(...childPaths: string[]): string {
    return this.site.getRouteUrl(
      "settings/reservations",
      String(this.id),
      ...childPaths,
    );
  }
}

export function deserializeReservationVendor(
  data: Pick<Api.Vendor, "id" | "name" | "vendorNumber"> &
    Partial<Pick<Api.Vendor, "doorCount">>,
  { site }: { readonly site: Site },
): ReservationVendor {
  return {
    ...deserializeVendorDisplayName({
      name: data.name,
      vendorNumber: data.vendorNumber,
    }),
    getRouteUrl: (...childPaths) =>
      getVendorRouteUrl({ id: data.id, site }, ...childPaths),
    id: data.id,
    maxAppointmentDoorCount: "doorCount" in data ? data.doorCount : undefined,
    name: data.name,
    number: data.vendorNumber,
  };
}

type HistoryFieldName =
  StringKeys<SharedApiV2.ReservationDetailDto> | "carrier" | "door" | "vendor";

export function isVendorInOrders(
  vendor: ReservationVendor,
  orders: readonly AppointmentUpdatePurchaseOrder[],
): boolean {
  for (const order of orders) {
    if (order.vendor.id === vendor.id) {
      return true;
    }

    const base = "base" in order ? order.base : order;

    if (base) {
      for (const details of base.orderDetails) {
        if (details.vendor.id === vendor.id) {
          return true;
        }
      }
    }
  }

  return false;
}
